import React, { useState, ChangeEvent, FormEvent, useEffect } from "react";
import Button from "../../components/ui/button/Button";
import { Table, TableHeader, TableBody, TableRow, TableCell } from "../../components/ui/table";
import { Modal } from "../../components/ui/modal";
import Badge from "../../components/ui/badge/Badge";
import type { AgenciaB2B, ReservaB2B, Payment } from "../../hooks/useApi";
import { DollarLineIcon, TimeIcon, CreditCardIcon } from "../../icons";
import CreateAgencyModal from "../../components/agencies/CreateAgencyModal";
import { toast } from 'react-hot-toast';
import * as XLSX from 'xlsx';
import useAuth from "../../hooks/useAuth";
import RoleBasedRenderer from "../../components/auth/RoleBasedRenderer";
import { Eye, Edit, Trash2, FileText, Calendar, Mail, Download } from "lucide-react";
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import useApi from "../../hooks/useApi";
import ReactDatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { API_URL, API_BASE_URL } from "../../config";

export interface AgencyListProps {
  agencies: AgenciaB2B[];
  searchTerm: string;
  onSearchChange: (value: string) => void;
  onAddAgency: (agency: Omit<AgenciaB2B, "id" | "reservations">) => Promise<AgenciaB2B>;
  onEditAgency: (agency: AgenciaB2B) => Promise<void>;
  onDeleteAgency: (agencyId: number) => void;
  getAgencyById: (id: number) => Promise<AgenciaB2B>;
  getAgencyReservations: (id: number) => Promise<ReservaB2B[]>;
  getAgencyPayments?: (id: number) => Promise<Payment[]>;
  filterStatus: string;
  onFilterChange: (value: string) => void;
}

// Añadir esta definición de tipo al inicio del archivo
type MetodoPago = "efectivo" | "tarjeta" | "transferencia";

// Interfaz de datos financieros para agencias
interface FinancialData {
  saldoPendiente: number;
  ultimoPago: string | null;
  historialPagos: ReservaB2B[];
  metodoPagos?: Payment[];
}

// Agregar esta interfaz para los tipos de correo
interface EmailTemplate {
  id: string;
  title: string;
  description: string;
  icon: React.ReactNode;
}

// Agregar los templates de correo disponibles
const emailTemplates: EmailTemplate[] = [
  {
    id: 'booking-confirmation',
    title: 'Confirmación de reservas',
    description: 'Envía un correo con los detalles de las reservas realizadas y la información de pago.',
    icon: (
      <svg className="w-6 h-6 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
    ),
  },
  {
    id: 'payment-reminder',
    title: 'Recordatorio de pago pendiente',
    description: 'Notifica sobre pagos pendientes y proporciona instrucciones para completar la transacción.',
    icon: (
      <svg className="w-6 h-6 text-yellow-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
    ),
  },
  {
    id: 'availability-alert',
    title: 'Alerta de disponibilidad',
    description: 'Informa sobre excursiones con plazas limitadas para promover reservas anticipadas.',
    icon: (
      <svg className="w-6 h-6 text-blue-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
      </svg>
    ),
  },
];

const AgencyList: React.FC<AgencyListProps> = ({
  agencies,
  searchTerm,
  onSearchChange,
  onAddAgency,
  onEditAgency,
  onDeleteAgency,
  getAgencyById,
  getAgencyReservations,
  getAgencyPayments,
  filterStatus,
  onFilterChange,
}) => {
  const agenciesData = agencies;

  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isReservationsModalOpen, setIsReservationsModalOpen] = useState(false);
  const [isFinancialModalOpen, setIsFinancialModalOpen] = useState(false);
  const [isEmailModalOpen, setIsEmailModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [agencyIdToDelete, setAgencyIdToDelete] = useState<number | null>(null);

  const [agencyReservations, setAgencyReservations] = useState<ReservaB2B[]>([]);
  const [selectedAgency, setSelectedAgency] = useState<AgenciaB2B | null>(null);

  const [newAgencyData, setNewAgencyData] = useState({
    name: "",
    email: "",
    phone: "",
    rncEmpresa: "",
  });

  const [editAgencyData, setEditAgencyData] = useState({
    name: "",
    email: "",
    phone: "",
    rncEmpresa: "",
    responsable: "",
    active: true
  });

  // Agregar el estado para el modal financiero
  const [financialData, setFinancialData] = useState<FinancialData>({
    saldoPendiente: 0,
    ultimoPago: null,
    historialPagos: []
  });

  // Add useEffect to refetch payment data when financial modal opens
  useEffect(() => {
    if (isFinancialModalOpen && selectedAgency && getAgencyPayments) {
      console.log("Financial modal opened, refetching payments for agency:", selectedAgency.id);
      
      const refetchPayments = async () => {
        try {
          const payments = await getAgencyPayments(selectedAgency.id);
          console.log("Refetched payments:", payments);
          
          if (payments && payments.length > 0) {
            setFinancialData(prev => ({
              ...prev,
              metodoPagos: payments
            }));
            console.log("Updated financial data with refetched payments");
          } else {
            console.log("No payments found in refetch");
          }
        } catch (error) {
          console.error("Error refetching payments:", error);
        }
      };
      
      refetchPayments();
    }
  }, [isFinancialModalOpen, selectedAgency, getAgencyPayments]);

  // Add useAuth hook
  const { canCreate, canEdit, canDelete } = useAuth();

  // Use the API hook
  const api = useApi();
  
  // Add a function to get auth headers
  const getHeaders = () => {
    const headers: Record<string, string> = {
      "Content-Type": "application/json",
    };

    const token = localStorage.getItem("token") || sessionStorage.getItem("token");
    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }

    return headers;
  };

  const handleAddAgencySubmit = async (e: FormEvent) => {
    e.preventDefault();
    
    try {
      if (!newAgencyData.name || !newAgencyData.email || !newAgencyData.phone) {
        toast.error("Por favor complete todos los campos requeridos");
        return;
      }

      const newAgency = await onAddAgency({
        ...newAgencyData,
        rncEmpresa: newAgencyData.rncEmpresa || undefined,
        active: true
      });

      setNewAgencyData({
        name: "",
        email: "",
        phone: "",
        rncEmpresa: "",
      });

      if (newAgency) {
        toast.success("Agencia creada exitosamente");
      }
      setIsAddModalOpen(false);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Error al crear la agencia");
    }
  };

  const handleEditAgencySubmit = async (e: FormEvent) => {
    e.preventDefault();
    
    try {
      if (!editAgencyData.name || !editAgencyData.email || !editAgencyData.phone) {
        toast.error("Por favor complete todos los campos requeridos");
        return;
      }

      if (selectedAgency) {
        await onEditAgency({
          ...selectedAgency,
          ...editAgencyData,
          rncEmpresa: editAgencyData.rncEmpresa || undefined
        });

        setEditAgencyData({
          name: "",
          email: "",
          phone: "",
          rncEmpresa: "",
          responsable: "",
          active: true
        });

        toast.success("Agencia actualizada exitosamente");
        setIsEditModalOpen(false);
      }
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Error al actualizar la agencia");
    }
  };

  const handleEditClick = (agency: AgenciaB2B) => {
    setSelectedAgency(agency);
    setEditAgencyData({
      ...agency,
      rncEmpresa: agency.rncEmpresa || "",
      responsable: agency.responsable || "",
    });
    setIsEditModalOpen(true);
  };

  const handleReservationsClick = async (agency: AgenciaB2B) => {
    try {
      const reservations = await getAgencyReservations(agency.id);
      setAgencyReservations(reservations);
      setSelectedAgency(agency);
      setIsReservationsModalOpen(true);
    } catch (error) {
      toast.error("Error al obtener el historial de reservas");
    }
  };

  // Agregar la función para manejar el click en el botón financiero
  const handleFinancialClick = async (agency: AgenciaB2B) => {
    try {
      const history = await getAgencyReservations(agency.id);
      
      // Calculate total pending balance
      const saldoPendiente = history.reduce((total, reserva) => total + reserva.saldoPendiente, 0);
      
      // Get payments from payment records (preferred method)
      let payments: Payment[] = [];
      if (getAgencyPayments) {
        try {
          payments = await getAgencyPayments(agency.id);
          console.log('Agency payments:', payments);
        } catch (error) {
          console.error("Error getting agency payments:", error);
        }
      }

      // Find latest payment date
      const ultimoPago = payments.length > 0 
        ? payments.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())[0].date
        : history.filter(res => res.montoFacturado > 0)
            .sort((a, b) => new Date(b.fecha).getTime() - new Date(a.fecha).getTime())[0]?.fecha || null;

      setFinancialData({
        saldoPendiente,
        ultimoPago,
        historialPagos: history.sort((a, b) => new Date(b.fecha).getTime() - new Date(a.fecha).getTime()),
        metodoPagos: payments
      });
      
      setSelectedAgency(agency);
      setIsFinancialModalOpen(true);
    } catch (error) {
      toast.error("Error al obtener la información financiera");
    }
  };

  // Agregar la función handleDetailsClick después de handleFinancialClick
  const handleDetailsClick = (agency: AgenciaB2B) => {
    setSelectedAgency(agency);
    setIsDetailsModalOpen(true);
  };

  function getStatusBadgeColor(status: string): "primary" | "success" | "error" | "warning" | "info" | "light" {
    switch (status) {
      case "pending_payment":
        return "primary";
      case "partial_payment":
        return "info";
      case "paid":
        return "success";
      case "no_show":
        return "warning";
      case "completed":
        return "success";
      case "cancelled":
        return "error";
      default:
        return "light";
    }
  }

  // Add these helper objects if they don't exist
  const estadoDisplay = {
    pending_payment: "Pendiente",
    partial_payment: "Pago parcial",
    paid: "Pagada",
    no_show: "No Show",
    completed: "Completada",
    cancelled: "Cancelada"
  };

  const [isDetailsModalOpen, setIsDetailsModalOpen] = useState(false);

  // Add state variables for invoice report generation inside the AgencyList component
  const [invoiceReportStartDate, setInvoiceReportStartDate] = useState<string>(() => {
    // Default to first day of current month
    const today = new Date();
    return format(new Date(today.getFullYear(), today.getMonth(), 1), 'yyyy-MM-dd');
  });
  const [invoiceReportEndDate, setInvoiceReportEndDate] = useState<string>(() => {
    // Default to today
    return format(new Date(), 'yyyy-MM-dd');
  });
  const [invoiceReportStatus, setInvoiceReportStatus] = useState<string>("");
  const [isGeneratingReport, setIsGeneratingReport] = useState(false);
  const [invoiceCount, setInvoiceCount] = useState<number | null>(null);
  const [invoiceReportError, setInvoiceReportError] = useState<string | null>(null);

  // Fix the fetchInvoiceCount function by removing duplicated '/api/' 
  const fetchInvoiceCount = async (agencyId: number) => {
    if (!agencyId || !invoiceReportStartDate || !invoiceReportEndDate) return;
    
    try {
      setInvoiceCount(null);
      
      // Use the API_URL constant from config
      const baseUrl = API_URL;
      
      // Build the URL with the proper domain - FIXING DUPLICATED '/api/'
      let endpoint = `/reports/agency/${agencyId}`;
      const params = new URLSearchParams();
      params.append('startDate', invoiceReportStartDate);
      params.append('endDate', invoiceReportEndDate);
      if (invoiceReportStatus) {
        params.append('estado', invoiceReportStatus);
      }
      
      // Complete URL with the base URL
      const requestUrl = `${baseUrl}${endpoint}?${params.toString()}`;
      
      console.log('Fetching invoice count from:', requestUrl);
      
      const response = await fetch(requestUrl, {
        method: 'GET',
        headers: getHeaders()
      });
      
      // Log response details for debugging
      console.log('Response status:', response.status);
      console.log('Response content type:', response.headers.get('content-type'));
      
      if (!response.ok) {
        throw new Error(`Error al consultar facturas: ${response.status} ${response.statusText}`);
      }
      
      // Check if response is JSON before parsing
      const contentType = response.headers.get('content-type');
      if (!contentType || !contentType.includes('application/json')) {
        console.error('Non-JSON response received:', await response.text());
        throw new Error('La respuesta del servidor no es JSON válido');
      }
      
      const data = await response.json();
      if (data.success && data.data) {
        setInvoiceCount(data.data.invoices.length);
      } else {
        setInvoiceCount(0);
      }
    } catch (error) {
      console.error('Error fetching invoice count:', error);
      setInvoiceCount(0);
    }
  };

  // Add useEffect to fetch invoice count when selectedAgency or date range changes
  useEffect(() => {
    if (isDetailsModalOpen && selectedAgency) {
      fetchInvoiceCount(selectedAgency.id);
    }
  }, [isDetailsModalOpen, selectedAgency, invoiceReportStartDate, invoiceReportEndDate, invoiceReportStatus]);

  // Fix the generateInvoiceReport function by removing duplicated '/api/'
  const generateInvoiceReport = async () => {
    if (!selectedAgency) return;
    
    try {
      setIsGeneratingReport(true);
      setInvoiceReportError(null);
      
      // Use the API_URL constant from config
      const baseUrl = API_URL;
      
      // Build the URL with the proper domain - FIXING DUPLICATED '/api/'
      let endpoint = `/reports/agency/${selectedAgency.id}`;
      const params = new URLSearchParams();
      params.append('startDate', invoiceReportStartDate);
      params.append('endDate', invoiceReportEndDate);
      params.append('format', 'pdf');
      if (invoiceReportStatus) {
        params.append('estado', invoiceReportStatus);
      }
      
      // Complete URL with the base URL
      const requestUrl = `${baseUrl}${endpoint}?${params.toString()}`;
      console.log('Generating invoice report from:', requestUrl);
      
      const response = await fetch(requestUrl, {
        method: 'GET',
        headers: getHeaders()
      });
      
      if (!response.ok) {
        // Log error details for debugging
        console.error('Error response status:', response.status);
        console.error('Error response content type:', response.headers.get('content-type'));
        
        // If it's probably HTML, get the text to see the error
        if (response.headers.get('content-type')?.includes('text/html')) {
          const errorText = await response.text();
          console.error('Error response HTML:', errorText.substring(0, 500) + '...');
        }
        
        throw new Error(`Error al generar el reporte: ${response.status} ${response.statusText}`);
      }
      
      // Get the blob from the response
      const blob = await response.blob();
      
      // Create a temporary URL to the blob
      const downloadUrl = window.URL.createObjectURL(blob);
      
      // Create a temporary link element to trigger the download
      const link = document.createElement('a');
      link.href = downloadUrl;
      
      // Create filename based on agency name and date range
      const fileName = `reporte_facturas_${selectedAgency.name.replace(/\s+/g, '_')}_${invoiceReportStartDate}_${invoiceReportEndDate}.pdf`;
      link.setAttribute('download', fileName);
      
      // Append to the document and trigger the download
      document.body.appendChild(link);
      link.click();
      
      // Clean up
      link.remove();
      window.URL.revokeObjectURL(downloadUrl);
      
      toast.success('Reporte generado con éxito');
    } catch (error) {
      console.error('Error generating invoice report:', error);
      setInvoiceReportError('Error al generar el reporte. Inténtelo de nuevo.');
      toast.error('Error al generar el reporte');
    } finally {
      setIsGeneratingReport(false);
    }
  };

  const filteredAgencies = agenciesData
    .filter(agency => {
      const matchesSearch = agency.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        agency.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
        agency.phone.toLowerCase().includes(searchTerm.toLowerCase());

      const matchesStatus = !filterStatus || 
        (filterStatus === 'active' && agency.active) || 
        (filterStatus === 'inactive' && !agency.active);

      return matchesSearch && matchesStatus;
    });

  const handleExportToExcel = () => {
    // Prepare the data for export
    const exportData = agencies.map(agency => ({
      'Nombre de la Agencia': agency.name,
      'Correo Electrónico': agency.email,
      'Teléfono': agency.phone,
      'Estado': agency.active ? 'Activo' : 'Inactivo',
      'Reservas': agency.reservations || 0,
    }));

    // Create workbook and worksheet
    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.json_to_sheet(exportData);

    // Append worksheet to workbook
    XLSX.utils.book_append_sheet(wb, ws, 'Agencias');

    // Generate filename with current date
    const date = new Date().toISOString().split('T')[0];
    const fileName = `agencias_${date}.xlsx`;

    // Save the file
    XLSX.writeFile(wb, fileName);
    toast.success('Archivo Excel generado correctamente');
  };

  // Add pagination state variables after existing state declarations
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10; // Number of agencies to show per page

  // Add this reset to first page effect after the component's existing useEffect blocks
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, filterStatus]);

  // Pagination controls
  const handleNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  const handlePrevPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  // Add these calculations after the filteredAgencies declaration
  // Calculate pagination values
  const totalPages = Math.ceil(filteredAgencies.length / itemsPerPage);
  const paginatedAgencies = filteredAgencies.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  return (
    <div>
      <div className="mb-6 flex flex-col md:flex-row md:items-center md:justify-between">
        <div>
          <h2 className="text-xl font-semibold text-gray-800 dark:text-gray-100">
            Gestión de agencias
          </h2>
          <p className="text-sm text-gray-600 dark:text-gray-300">
            {filteredAgencies.length} {filteredAgencies.length === 1 ? 'agencia encontrada' : 'agencias encontradas'}
          </p>
        </div>
        <div className="flex flex-col md:flex-row gap-2">
          <div className="flex flex-col md:flex-row gap-2">
            <input
              type="text"
              placeholder="Buscar agencias..."
              value={searchTerm}
              onChange={(e: ChangeEvent<HTMLInputElement>) => onSearchChange(e.target.value)}
              className="rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 px-3 py-2 text-sm text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400"
            />
            <select
              value={filterStatus}
              onChange={(e: ChangeEvent<HTMLSelectElement>) => onFilterChange(e.target.value)}
              className="rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 px-3 py-2 text-sm text-gray-900 dark:text-gray-100"
            >
              <option value="">Todos</option>
              <option value="active">Activos</option>
              <option value="inactive">Inactivos</option>
            </select>
          </div>
          <div className="flex gap-2">
            <RoleBasedRenderer requiredPermission="create">
              <Button variant="primary" onClick={() => setIsAddModalOpen(true)} className="w-full md:w-auto">
                Agregar agencia
              </Button>
            </RoleBasedRenderer>
            <Button variant="outline" onClick={handleExportToExcel} className="w-full md:w-auto">
              Exportar a Excel
            </Button>
          </div>
        </div>
      </div>

      <div className="mt-6">
        <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-700">
          <Table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <TableHeader className="bg-gray-50 dark:bg-gray-800">
              <TableRow>
                <TableCell isHeader className="w-[35%] px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-300">
                  Nombre de la agencia
                </TableCell>
                <TableCell isHeader className="w-[25%] px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-300">
                  Responsable
                </TableCell>
                <TableCell isHeader className="w-[15%] px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-300">
                  Reservas
                </TableCell>
                <TableCell isHeader className="w-[10%] px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-300">
                  Estado
                </TableCell>
                <TableCell isHeader className="w-[15%] px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-300">
                  Acciones
                </TableCell>
              </TableRow>
            </TableHeader>
            <TableBody className="bg-white dark:bg-gray-900 divide-y divide-gray-200 dark:divide-gray-700">
              {paginatedAgencies.length > 0 ? (
                paginatedAgencies.map((agency) => (
                  <TableRow key={agency.id} className="hover:bg-gray-50 dark:hover:bg-gray-800">
                    <TableCell className="px-4 py-4 text-sm text-gray-900 dark:text-gray-100 whitespace-nowrap">
                      {agency.name}
                    </TableCell>
                    <TableCell className="px-4 py-4 text-sm text-gray-900 dark:text-gray-100 whitespace-nowrap">
                      {agency.responsable || "No especificado"}
                    </TableCell>
                    <TableCell className="px-4 py-4 text-sm text-gray-900 dark:text-gray-100 whitespace-nowrap">
                      {agency.reservations}
                    </TableCell>
                    <TableCell className="px-4 py-4 text-sm whitespace-nowrap">
                      <Badge variant="light" color={agency.active ? "success" : "error"}>
                        {agency.active ? "Activa" : "Inactiva"}
                      </Badge>
                    </TableCell>
                    <TableCell className="px-4 py-4 text-sm whitespace-nowrap">
                      <div className="flex space-x-2 items-center">
                        <Button 
                          variant="outline" 
                          size="sm"
                          className="p-2"
                          aria-label="Reservas"
                          onClick={() => handleReservationsClick(agency)}
                        >
                          <Calendar size={16} />
                        </Button>
                        <Button 
                          variant="outline" 
                          size="sm"
                          className="p-2"
                          aria-label="Financiero"
                          onClick={() => handleFinancialClick(agency)}
                        >
                          <DollarLineIcon className="w-4 h-4" />
                        </Button>
                        <Button 
                          variant="outline" 
                          size="sm"
                          className="p-2"
                          aria-label="Detalles"
                          onClick={() => handleDetailsClick(agency)}
                        >
                          <Eye size={16} />
                        </Button>
                        <RoleBasedRenderer requiredPermission="edit">
                          <Button 
                            variant="outline" 
                            size="sm"
                            className="p-2"
                            aria-label="Editar"
                            onClick={() => handleEditClick(agency)}
                          >
                            <Edit size={16} />
                          </Button>
                        </RoleBasedRenderer>
                        <RoleBasedRenderer requiredPermission="delete">
                          <Button 
                            variant="outline" 
                            size="sm"
                            className="p-2"
                            aria-label="Eliminar"
                            onClick={() => {
                              setAgencyIdToDelete(agency.id);
                              setIsDeleteModalOpen(true);
                            }}
                          >
                            <Trash2 size={16} />
                          </Button>
                        </RoleBasedRenderer>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={5} className="px-4 py-4 text-sm text-center text-gray-500 dark:text-gray-400">
                    {searchTerm ? `No se encontraron resultados para "${searchTerm}"` : "No hay agencias registradas"}
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>
      </div>

      <CreateAgencyModal
        isOpen={isAddModalOpen}
        onClose={() => setIsAddModalOpen(false)}
        onAddAgency={onAddAgency}
        onSuccess={(newAgency) => {
          // La lista se actualizará automáticamente por el estado en el componente padre
          setIsAddModalOpen(false);
        }}
      />

      <Modal isOpen={isEditModalOpen} onClose={() => setIsEditModalOpen(false)} title="Editar agencia">
        <form onSubmit={handleEditAgencySubmit} className="space-y-4">
          <div>
            <label htmlFor="edit-name" className="block text-sm font-medium text-gray-700 dark:text-gray-200">
              Nombre de la agencia
            </label>
            <input
              type="text"
              id="edit-name"
              value={editAgencyData.name}
              onChange={(e) => setEditAgencyData({ ...editAgencyData, name: e.target.value })}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-brand-500 focus:ring-brand-500 dark:bg-gray-800 dark:border-gray-600 dark:text-white sm:text-sm"
              required
            />
          </div>

          <div>
            <label htmlFor="edit-responsable" className="block text-sm font-medium text-gray-700 dark:text-gray-200">
              Responsable
            </label>
            <input
              type="text"
              id="edit-responsable"
              value={editAgencyData.responsable}
              onChange={(e) => setEditAgencyData({ ...editAgencyData, responsable: e.target.value })}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-brand-500 focus:ring-brand-500 dark:bg-gray-800 dark:border-gray-600 dark:text-white sm:text-sm"
              placeholder="Nombre del responsable de la agencia"
            />
          </div>

          <div>
            <label htmlFor="edit-email" className="block text-sm font-medium text-gray-700 dark:text-gray-200">
              Correo electrónico
            </label>
            <input
              type="email"
              id="edit-email"
              value={editAgencyData.email}
              onChange={(e) => setEditAgencyData({ ...editAgencyData, email: e.target.value })}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-brand-500 focus:ring-brand-500 dark:bg-gray-800 dark:border-gray-600 dark:text-white sm:text-sm"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Teléfono</label>
            <input
              type="text"
              value={editAgencyData.phone}
              onChange={(e) => setEditAgencyData({ ...editAgencyData, phone: e.target.value })}
              className="mt-1 block w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 px-3 py-2 text-sm text-gray-900 dark:text-gray-100"
              required
            />
          </div>

          <div>
            <label htmlFor="edit-rncEmpresa" className="block text-sm font-medium text-gray-700 dark:text-gray-200">
              RNC Empresa
            </label>
            <input
              type="text"
              id="edit-rncEmpresa"
              value={editAgencyData.rncEmpresa}
              onChange={(e) => setEditAgencyData({ ...editAgencyData, rncEmpresa: e.target.value })}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-brand-500 focus:ring-brand-500 dark:bg-gray-800 dark:border-gray-600 dark:text-white sm:text-sm"
            />
          </div>

          <div className="relative flex items-start">
            <div className="flex h-6 items-center">
              <input
                type="checkbox"
                id="edit-active"
                checked={editAgencyData.active}
                onChange={(e) => setEditAgencyData({ ...editAgencyData, active: e.target.checked })}
                className="h-4 w-4 rounded border-gray-300 text-brand-600 focus:ring-brand-500 dark:border-gray-600 dark:bg-gray-800 dark:ring-offset-gray-800"
              />
            </div>
            <div className="ml-3 text-sm leading-6">
              <label htmlFor="edit-active" className="font-medium text-gray-900 dark:text-gray-100">
                Agencia activa
              </label>
            </div>
          </div>

          <div className="flex justify-end gap-3 pt-4">
            <Button variant="outline" onClick={() => setIsEditModalOpen(false)} type="button">
              Cancelar
            </Button>
            <Button variant="primary" type="submit">
              Guardar cambios
            </Button>
          </div>
        </form>
      </Modal>

      <Modal 
        isOpen={isReservationsModalOpen} 
        onClose={() => setIsReservationsModalOpen(false)} 
        title={`Historial de reservas ${selectedAgency ? `de ${selectedAgency.name}` : ""}`}
        size="xl"
      >
        <div className="max-h-[calc(100vh-200px)] overflow-y-auto">
          <div className="overflow-x-auto">
            {agencyReservations.length > 0 ? (
              <div className="min-w-full">
                <Table className="w-full">
                  <TableHeader className="bg-gray-50 dark:bg-gray-800 sticky top-0 z-10">
                    <TableRow>
                      <TableCell isHeader className="w-[30%] px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-300">
                        Excursión
                      </TableCell>
                      <TableCell isHeader className="w-[12%] px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-300">
                        Fecha
                      </TableCell>
                      <TableCell isHeader className="w-[18%] px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-300">
                        Personas
                      </TableCell>
                      <TableCell isHeader className="w-[12%] px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-300">
                        Estado
                      </TableCell>
                      <TableCell isHeader className="w-[10%] px-4 py-3 text-right text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-300">
                        Total
                      </TableCell>
                      <TableCell isHeader className="w-[10%] px-4 py-3 text-right text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-300">
                        Facturado
                      </TableCell>
                      <TableCell isHeader className="w-[8%] px-4 py-3 text-right text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-300">
                        Pendiente
                      </TableCell>
                    </TableRow>
                  </TableHeader>
                  <TableBody className="bg-white dark:bg-gray-900 divide-y divide-gray-200 dark:divide-gray-700">
                    {agencyReservations.map((res) => (
                      <TableRow key={res.id} className="hover:bg-gray-50 dark:hover:bg-gray-800">
                        <TableCell className="px-4 py-3 text-sm text-gray-900 dark:text-gray-100 truncate">
                          {res.excursion}
                        </TableCell>
                        <TableCell className="px-4 py-3 text-sm text-gray-900 dark:text-gray-100 whitespace-nowrap">
                          {new Date(res.fechaISO || res.fecha).toLocaleDateString('es-ES', {
                            day: 'numeric',
                            month: 'short',
                            year: 'numeric'
                          })}
                        </TableCell>
                        <TableCell className="px-4 py-3 text-sm text-gray-900 dark:text-gray-100 whitespace-nowrap">
                          {res.adultos} adultos{res.ninios > 0 && `, ${res.ninios} niños`}
                        </TableCell>
                        <TableCell className="px-4 py-3 text-sm whitespace-nowrap">
                          <Badge color={getStatusBadgeColor(res.estado)}>
                            {estadoDisplay[res.estado]}
                          </Badge>
                        </TableCell>
                        <TableCell className="px-4 py-3 text-sm text-right text-gray-900 dark:text-gray-100 whitespace-nowrap">
                          ${res.precioTotal ? res.precioTotal.toFixed(2) : '0.00'}
                        </TableCell>
                        <TableCell className="px-4 py-3 text-sm text-right text-gray-900 dark:text-gray-100 whitespace-nowrap">
                          ${res.montoFacturado ? res.montoFacturado.toFixed(2) : '0.00'}
                        </TableCell>
                        <TableCell className="px-4 py-3 text-sm text-right text-gray-900 dark:text-gray-100 whitespace-nowrap">
                          ${res.saldoPendiente ? res.saldoPendiente.toFixed(2) : '0.00'}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            ) : (
              <p className="text-center py-4 text-gray-500 dark:text-gray-400">
                No se encontraron reservas.
              </p>
            )}
          </div>
        </div>
      </Modal>

      <Modal
        isOpen={isDeleteModalOpen}
        onClose={() => {
          setIsDeleteModalOpen(false);
          setAgencyIdToDelete(null);
        }}
        title="Confirmación de eliminación"
        size="md"
      >
        <div className="space-y-6 px-2">
          <div className="space-y-4">
            <p className="text-lg font-medium text-gray-900 dark:text-gray-100">
              ¿Está seguro de eliminar esta agencia?
            </p>
            
            <div className="space-y-3">
              <p className="text-sm font-medium text-red-600 dark:text-red-400">
                Esta acción eliminará permanentemente todos los datos asociados a la agencia, incluyendo:
              </p>
              
              <ul className="list-disc list-inside text-sm text-gray-700 dark:text-gray-300 ml-4 space-y-1">
                <li>Información de la agencia</li>
                <li>Historial completo de reservas</li>
                <li>Registros de pagos y transacciones</li>
                <li>Tarifas personalizadas</li>
                <li>Cualquier otro dato relacionado</li>
              </ul>
            </div>

            <div className="mt-4 p-3 bg-red-50 dark:bg-red-900/10 border border-red-100 dark:border-red-900/20 rounded-lg">
              <p className="text-sm font-medium text-red-600 dark:text-red-400">
                Esta acción no se puede deshacer.
              </p>
            </div>
          </div>

          <div className="flex justify-end gap-3 pt-2">
            <Button
              variant="outline"
              onClick={() => {
                setIsDeleteModalOpen(false);
                setAgencyIdToDelete(null);
              }}
            >
              Cancelar
            </Button>
            <Button
              variant="primary"
              onClick={async () => {
                if (agencyIdToDelete !== null) {
                  try {
                    await onDeleteAgency(agencyIdToDelete);
                    toast.success("Agencia eliminada exitosamente");
                    setIsDeleteModalOpen(false);
                    setAgencyIdToDelete(null);
                  } catch (error) {
                    toast.error("Error al eliminar la agencia");
                  }
                }
              }}
            >
              Sí, eliminar agencia
            </Button>
          </div>
        </div>
      </Modal>

      {/* Modal Financiero */}
      <Modal
        isOpen={isFinancialModalOpen}
        onClose={() => setIsFinancialModalOpen(false)}
        title="Información financiera de la agencia"
        size="lg"
      >
        <div className="space-y-4 p-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Pagos Pendientes */}
            <div className="flex flex-col p-3 bg-white dark:bg-gray-800/50 rounded-xl border border-gray-200 dark:border-gray-700">
              <div className="flex items-center gap-3 mb-2">
                <div className="p-2 bg-red-50 dark:bg-red-900/20 rounded-lg">
                  <DollarLineIcon className="w-5 h-5 text-red-500 dark:text-red-400" />
                </div>
                <h3 className="text-sm font-medium text-gray-900 dark:text-white">
                  Saldo pendiente
                </h3>
              </div>
              <p className={`text-xl font-semibold ${
                financialData.saldoPendiente > 0 
                  ? 'text-red-600 dark:text-red-400' 
                  : 'text-green-600 dark:text-green-400'
              }`}>
                ${financialData.saldoPendiente ? financialData.saldoPendiente.toFixed(2) : '0.00'}
              </p>
              <p className="text-xs text-gray-600 dark:text-gray-300 mt-1">
                {financialData.saldoPendiente > 0 ? 'Tiene pagos pendientes' : 'Al día con los pagos'}
              </p>
            </div>

            {/* Fecha de Último Pago */}
            <div className="flex flex-col p-3 bg-white dark:bg-gray-800/50 rounded-xl border border-gray-200 dark:border-gray-700">
              <div className="flex items-center gap-3 mb-2">
                <div className="p-2 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                  <TimeIcon className="w-5 h-5 text-blue-500 dark:text-blue-400" />
                </div>
                <h3 className="text-sm font-medium text-gray-900 dark:text-white">
                  Último pago
                </h3>
              </div>
              <p className="text-xl font-semibold text-gray-900 dark:text-white">
                {financialData.ultimoPago ? (
                  new Date(financialData.ultimoPago).toLocaleDateString('es-ES', {
                    day: 'numeric',
                    month: 'short',
                    year: 'numeric'
                  })
                ) : (
                  "Sin pagos"
                )}
              </p>
              <p className="text-xs text-gray-600 dark:text-gray-300 mt-1">
                Fecha del último pago registrado
              </p>
            </div>
          </div>

          {/* Historial de Pagos Recientes */}
          <div className="mt-6">
            <div className="flex items-center justify-between mb-3">
              <h4 className="text-sm font-medium text-gray-900 dark:text-white">
                Historial de pagos
              </h4>
              <span className="text-xs text-gray-500 dark:text-gray-400">
                {financialData.metodoPagos ? financialData.metodoPagos.length : 
                 financialData.historialPagos.reduce((total, reserva) => 
                  total + ((reserva.montoFacturado > 0) ? 1 : 0), 0)} pagos
              </span>
            </div>
            <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-700">
              <div className="max-h-[400px] overflow-y-auto scrollbar-thin scrollbar-thumb-gray-300 dark:scrollbar-thumb-gray-600 scrollbar-track-gray-100 dark:scrollbar-track-gray-800">
                <Table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                  <TableHeader className="bg-gray-50 dark:bg-gray-800 sticky top-0 z-10">
                    <TableRow>
                      <TableCell isHeader className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-300">
                        Fecha
                      </TableCell>
                      <TableCell isHeader className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-300">
                        Excursión
                      </TableCell>
                      <TableCell isHeader className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-300">
                        Monto pagado
                      </TableCell>
                      <TableCell isHeader className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-300">
                        Método
                      </TableCell>
                    </TableRow>
                  </TableHeader>
                  <TableBody className="bg-white dark:bg-gray-900 divide-y divide-gray-200 dark:divide-gray-700">
                    {financialData.metodoPagos && financialData.metodoPagos.length > 0 ? (
                      // Display all payments from payment records
                      financialData.metodoPagos
                        .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())
                        .map(payment => (
                          <TableRow key={`payment-${payment.id}`} className="hover:bg-gray-50 dark:hover:bg-gray-800">
                            <TableCell className="px-4 py-3 whitespace-nowrap text-sm text-gray-500 dark:text-gray-300">
                              {new Date(payment.date).toLocaleDateString('es-ES', {
                                day: 'numeric',
                                month: 'short',
                                year: 'numeric'
                              })}
                            </TableCell>
                            <TableCell className="px-4 py-3 whitespace-nowrap text-sm text-gray-500 dark:text-gray-300">
                              {payment.reservation?.excursion || 
                               (payment.reservationId ? `Reserva #${payment.reservationId}` : 'Pago directo')}
                            </TableCell>
                            <TableCell className="px-4 py-3 whitespace-nowrap text-sm text-gray-500 dark:text-gray-300">
                              ${payment.amount.toFixed(2)}
                            </TableCell>
                            <TableCell className="px-4 py-3 whitespace-nowrap text-sm text-gray-500 dark:text-gray-300 capitalize">
                              {payment.method || 'efectivo'}
                            </TableCell>
                          </TableRow>
                        ))
                    ) : (
                      // Fallback message if no payments
                      <TableRow>
                        <TableCell colSpan={4} className="px-4 py-4 text-sm text-center text-gray-500 dark:text-gray-400">
                          No se encontraron pagos registrados
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </div>
            </div>
          </div>
        </div>
      </Modal>

      <Modal
        isOpen={isDetailsModalOpen}
        onClose={() => setIsDetailsModalOpen(false)}
        title={selectedAgency?.name || "Detalles de la agencia"}
        size="md"
      >
        <div className="space-y-4">
          {/* Responsable de la agencia */}
          <div className="bg-white dark:bg-gray-800 rounded-lg p-4 border border-gray-200 dark:border-gray-700">
            <h3 className="text-sm font-medium text-gray-900 dark:text-gray-100 mb-3">
              Responsable
            </h3>
            <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
              {selectedAgency?.responsable || "No especificado"}
            </p>
          </div>
          
          {/* Información Principal y Estado */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {/* Información de contacto */}
            <div className="bg-white dark:bg-gray-800 rounded-lg p-4 border border-gray-200 dark:border-gray-700">
              <h3 className="text-sm font-medium text-gray-900 dark:text-gray-100 mb-3">
                Información de contacto
              </h3>
              <div className="space-y-3">
                <div>
                  <p className="text-xs text-gray-500 dark:text-gray-400">Email</p>
                  <p className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
                    {selectedAgency?.email}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-gray-500 dark:text-gray-400">Teléfono</p>
                  <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
                    {selectedAgency?.phone}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-gray-500 dark:text-gray-400">RNC Empresa</p>
                  <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
                    {selectedAgency?.rncEmpresa || "No especificado"}
                  </p>
                </div>
              </div>
            </div>

            {/* Estado y Métricas */}
            <div className="space-y-4">
              {/* Estado */}
              <div className="bg-white dark:bg-gray-800 rounded-lg p-4 border border-gray-200 dark:border-gray-700">
                <h3 className="text-sm font-medium text-gray-900 dark:text-gray-100 mb-2">
                  Estado
                </h3>
                <div className="flex items-center justify-between">
                  <Badge color={selectedAgency?.active ? "success" : "error"}>
                    {selectedAgency?.active ? "Activo" : "Inactivo"}
                  </Badge>
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    Estado actual
                  </p>
                </div>
              </div>

              {/* Reservas */}
              <div className="bg-white dark:bg-gray-800 rounded-lg p-4 border border-gray-200 dark:border-gray-700">
                <h3 className="text-sm font-medium text-gray-900 dark:text-gray-100 mb-2">
                  Total de reservas
                </h3>
                <div className="flex items-center justify-between">
                  <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                    {selectedAgency?.reservations || 0}
                  </p>
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    Reservas realizadas
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Información Adicional */}
          <div className="grid grid-cols-1 gap-4">
            {/* Tarifas */}
            <div className="bg-white dark:bg-gray-800 rounded-lg p-4 border border-gray-200 dark:border-gray-700">
              <h3 className="text-sm font-medium text-gray-900 dark:text-gray-100 mb-2">
                Tarifas personalizadas
              </h3>
              <p className="text-sm text-gray-900 dark:text-gray-100">
                {"No especificadas"}
              </p>
            </div>
          </div>

          {/* Add a new section for Invoice Reports */}
          <div className="bg-white dark:bg-gray-800 rounded-lg p-4 border border-gray-200 dark:border-gray-700">
            <h3 className="text-sm font-medium text-gray-900 dark:text-gray-100 mb-3 flex items-center">
              <FileText className="w-4 h-4 mr-1.5 text-indigo-500 dark:text-indigo-400" />
              Reportes de Facturación
            </h3>
            
            <div className="space-y-3">
              {/* Date range inputs - using ReactDatePicker */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Fecha inicio
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <Calendar className="h-4 w-4 text-gray-400" />
                    </div>
                    <ReactDatePicker
                      selected={invoiceReportStartDate ? new Date(invoiceReportStartDate) : null}
                      onChange={(date: Date | null) => {
                        if (date) {
                          setInvoiceReportStartDate(format(date, 'yyyy-MM-dd'));
                        }
                      }}
                      className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 pl-10 pr-3 py-2 text-sm text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
                      dateFormat="yyyy-MM-dd"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Fecha fin
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <Calendar className="h-4 w-4 text-gray-400" />
                    </div>
                    <ReactDatePicker
                      selected={invoiceReportEndDate ? new Date(invoiceReportEndDate) : null}
                      onChange={(date: Date | null) => {
                        if (date) {
                          setInvoiceReportEndDate(format(date, 'yyyy-MM-dd'));
                        }
                      }}
                      className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 pl-10 pr-3 py-2 text-sm text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
                      dateFormat="yyyy-MM-dd"
                    />
                  </div>
                </div>
              </div>
              
              {/* Status dropdown - keep as is */}
              <div>
                <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Estado de facturas (opcional)
                </label>
                <select
                  value={invoiceReportStatus}
                  onChange={(e) => setInvoiceReportStatus(e.target.value)}
                  className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 px-3 py-2 text-sm text-gray-900 dark:text-gray-100"
                >
                  <option value="">Todas</option>
                  <option value="pendiente">Pendientes</option>
                  <option value="pagada">Pagadas</option>
                  <option value="anulada">Anuladas</option>
                  <option value="abonada">Abonadas</option>
                </select>
              </div>
              
              {/* Count and status indicator */}
              <div className="text-xs text-gray-500 dark:text-gray-400 h-4">
                {invoiceCount !== null && (
                  <>
                    {invoiceCount > 0 ? (
                      <span>{invoiceCount} facturas encontradas en el rango seleccionado</span>
                    ) : (
                      <span className="text-yellow-500 dark:text-yellow-400">
                        No se encontraron facturas en el rango seleccionado
                      </span>
                    )}
                  </>
                )}
                
                {invoiceReportError && (
                  <span className="text-red-500 dark:text-red-400">{invoiceReportError}</span>
                )}
              </div>
              
              {/* Generate button */}
              <Button
                variant="primary"
                size="sm"
                className="w-full"
                onClick={generateInvoiceReport}
                disabled={isGeneratingReport || invoiceCount === 0}
              >
                {isGeneratingReport ? (
                  <>
                    <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Generando...
                  </>
                ) : (
                  <>
                    <Download className="w-4 h-4 mr-1.5" />
                    Generar Reporte PDF
                  </>
                )}
              </Button>
            </div>
          </div>

          <div className="flex justify-end pt-4">
            <Button variant="primary" onClick={() => setIsDetailsModalOpen(false)}>
              Cerrar
            </Button>
          </div>
        </div>
      </Modal>

      <Modal
        isOpen={isEmailModalOpen}
        onClose={() => setIsEmailModalOpen(false)}
        title="Enviar correo electrónico"
        size="lg"
      >
        <div className="space-y-6">
          <div className="text-sm text-gray-500 dark:text-gray-400">
            Selecciona el tipo de correo que deseas enviar a <span className="font-medium text-gray-900 dark:text-gray-100">{selectedAgency?.name}</span>
          </div>

          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {emailTemplates.map((template) => (
              <button
                key={template.id}
                onClick={() => {
                  // Aquí iría la lógica para enviar el correo
                  toast.success(`Correo de ${template.title.toLowerCase()} enviado correctamente`);
                  setIsEmailModalOpen(false);
                }}
                className="relative flex flex-col items-center p-6 bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 transition-all hover:border-indigo-500 dark:hover:border-indigo-500 hover:shadow-md group"
              >
                <div className="mb-4 p-3 rounded-full bg-gray-50 dark:bg-gray-700 group-hover:bg-indigo-50 dark:group-hover:bg-indigo-900/30 transition-colors">
                  {template.icon}
                </div>
                <h3 className="text-sm font-medium text-gray-900 dark:text-gray-100 text-center mb-2">
                  {template.title}
                </h3>
                <p className="text-xs text-gray-500 dark:text-gray-400 text-center">
                  {template.description}
                </p>
                <div className="absolute inset-0 border-2 border-transparent rounded-xl group-hover:border-indigo-500 pointer-events-none transition-colors" />
              </button>
            ))}
          </div>

          <div className="bg-gray-50 dark:bg-gray-800/50 rounded-lg p-4">
            <div className="flex items-start">
              <div className="flex-shrink-0">
                <svg className="w-5 h-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <div className="ml-3">
                <h3 className="text-sm font-medium text-gray-900 dark:text-gray-100">
                  Información importante
                </h3>
                <div className="mt-2 text-sm text-gray-500 dark:text-gray-400">
                  <p>
                    Los correos se enviarán a la dirección registrada: <span className="font-medium text-gray-900 dark:text-gray-100">{selectedAgency?.email}</span>
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </Modal>

      {/* Add pagination controls after the table component */}
      {filteredAgencies.length > itemsPerPage && (
        <div className="flex items-center justify-between px-4 py-3 sm:px-6 border-t border-gray-200 dark:border-gray-700 mt-4">
          <div className="flex-1 flex justify-between sm:hidden">
            <button
              onClick={handlePrevPage}
              disabled={currentPage === 1}
              className={`relative inline-flex items-center px-4 py-2 text-sm font-medium rounded-md 
                ${currentPage === 1 
                  ? 'text-gray-300 dark:text-gray-600 cursor-not-allowed' 
                  : 'text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-800'} 
                border border-gray-300 dark:border-gray-600`}
            >
              Anterior
            </button>
            <button
              onClick={handleNextPage}
              disabled={currentPage === totalPages}
              className={`ml-3 relative inline-flex items-center px-4 py-2 text-sm font-medium rounded-md
                ${currentPage === totalPages 
                  ? 'text-gray-300 dark:text-gray-600 cursor-not-allowed' 
                  : 'text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-800'} 
                border border-gray-300 dark:border-gray-600`}
            >
              Siguiente
            </button>
          </div>
          <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
            <div>
              <p className="text-sm text-gray-700 dark:text-gray-300">
                Mostrando <span className="font-medium">{Math.min((currentPage - 1) * itemsPerPage + 1, filteredAgencies.length)}</span> a{" "}
                <span className="font-medium">{Math.min(currentPage * itemsPerPage, filteredAgencies.length)}</span> de{" "}
                <span className="font-medium">{filteredAgencies.length}</span> agencias
              </p>
            </div>
            <div>
              <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                <button
                  onClick={handlePrevPage}
                  disabled={currentPage === 1}
                  className={`relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 dark:border-gray-600 text-sm font-medium
                    ${currentPage === 1 
                      ? 'text-gray-300 dark:text-gray-600 cursor-not-allowed' 
                      : 'text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-800'}`}
                >
                  <span className="sr-only">Anterior</span>
                  <svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                    <path fillRule="evenodd" d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                </button>
                
                {/* Page numbers - limited to 5 pages */}
                {(() => {
                  // Calculate which page numbers to show (max 5)
                  let startPage = Math.max(1, currentPage - 2);
                  let endPage = Math.min(totalPages, startPage + 4);
                  
                  // Adjust startPage if we're near the end
                  if (endPage - startPage < 4 && startPage > 1) {
                    startPage = Math.max(1, endPage - 4);
                  }
                  
                  const pages = [];
                  for (let i = startPage; i <= endPage; i++) {
                    pages.push(
                      <button
                        key={i}
                        onClick={() => setCurrentPage(i)}
                        className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium
                          ${currentPage === i
                            ? 'z-10 bg-indigo-50 dark:bg-indigo-900/30 border-indigo-500 dark:border-indigo-500 text-indigo-600 dark:text-indigo-400'
                            : 'bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-600 text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700'}`}
                      >
                        {i}
                      </button>
                    );
                  }
                  return pages;
                })()}
                
                <button
                  onClick={handleNextPage}
                  disabled={currentPage === totalPages}
                  className={`relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 dark:border-gray-600 text-sm font-medium
                    ${currentPage === totalPages 
                      ? 'text-gray-300 dark:text-gray-600 cursor-not-allowed' 
                      : 'text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-800'}`}
                >
                  <span className="sr-only">Siguiente</span>
                  <svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                    <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
                  </svg>
                </button>
              </nav>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AgencyList;
export type { AgenciaB2B };